import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from './AuthProvider';
import { fetchNotifications, fetchQuoteById, markNotificationRead, getUsersByEmail } from './CommonContex';
import { usePresenceContext } from './PresenceContext';
import {
    getCommentThreadKey,
    getOrderThreadKey,
    isOrderOnlyThreadKey,
    threadMatchesOrder,
    useCommentDock,
} from './CommentDockContext';
import orderContext from './OrderContext';
import { extractCommentText, getCommentId, isQuoteLikeRecord, normalizeComment, sortCommentsNewestFirst } from '../utils/commentText';

/**
 * NotificationsProvider
 * ------------------------------------------------------------------
 * One source of truth for comment notifications, shared by:
 *   - the bell (Topbar),
 *   - the minimized comment chats in the bottom-left tray (CommentDockHost),
 *   - the comment icon in each Orders and Quotes row.
 *
 * Rules that keep the three in sync:
 *   1. Every order with unread comments has a chat in the tray (added minimized).
 *   2. The bell count = the sum of the tray badges = comments not yet opened.
 *   3. A chat that is open on screen marks its order's notifications read.
 *   4. Dismissing a chat from the tray marks its notifications read.
 *   A minimized chat stays blue until that user opens or dismisses it, even if a
 *   later poll reports the notification as read. The "New" pill is shown once,
 *   the first time the chat is opened, and is not shown again after a refresh.
 *
 * New comments arrive by polling (every 30 s while the tab is visible), immediately
 * when anyone saves a comment on any Bridge page (presence socket), and when a web push
 * reaches the service worker.
 */

const POLL_MS = 30 * 1000;
const PRESENCE_REFRESH_DELAY_MS = 800;
const CACHE_KEY = 'orderNotifications';
const FORCED_UNREAD_KEY = 'orderNotifications.forcedUnread';
const RELOAD_GUARD_MS = 2500;

const attentionStorageKey = (email) => `orderNotifications.attention:${email || 'anonymous'}`;
const seenNewStorageKey = (email) => `orderNotifications.seenNew:${email || 'anonymous'}`;

/** Comment ids already shown with a "New" pill during this page load. Dies on refresh. */
const shownNewThisPageLoad = new Set();

const NotificationsContext = createContext(null);

const readJson = (key, fallback) => {
    try {
        const raw = localStorage.getItem(key);
        return raw ? JSON.parse(raw) : fallback;
    } catch (err) {
        return fallback;
    }
};

const writeJson = (key, value) => {
    try {
        localStorage.setItem(key, JSON.stringify(value));
    } catch (err) {
        /* private mode / quota: the in-memory state still works */
    }
};

export const getNotificationId = (n) => (n?.notificationId ?? n?.id ?? null);

export const getNotificationOrderNumber = (n) => {
    const value = n?.orderNumber ?? n?.ordernum ?? n?.order_number ?? null;
    if (value == null) return null;
    const text = String(value).trim();
    return text && text.toUpperCase() !== 'N/A' ? text : null;
};

export const getNotificationLineId = (n) => {
    const value = n?.orderLineId ?? n?.orderlineId ?? n?.order_line_id ?? n?.lineId ?? null;
    return value == null || value === '' ? null : value;
};

const QUOTE_FLOWS = new Set(['quote', 'quotes']);
const PO_FLOWS = new Set(['purchase order', 'purchase orders', 'purchasing', 'po']);
/** Quote ids we have already seen this page load, so a later server row that only
 *  carries that id as an order number still belongs to the quote chat. */
const seenQuoteIds = new Set();
/** PO lines and PO numbers seen live this page load, so a later server row that only
 *  carries the PO number still belongs to that purchase-order chat. */
const seenPoLineIds = new Set();
const seenPoNumbers = new Set();

const rememberQuoteId = (quoteId) => {
    if (quoteId == null || quoteId === '') return;
    seenQuoteIds.add(String(quoteId));
};

/** Quote id on a notification, when the comment belongs to a quote rather than an order. */
export const getNotificationQuoteId = (n) => {
    const explicit = n?.quoteId ?? n?.quotesId ?? n?.quote_id ?? n?.quotes_id ?? null;
    if (explicit != null && explicit !== '') {
        const text = String(explicit).trim();
        if (text && text.toUpperCase() !== 'N/A') return text;
    }
    const flow = String(n?.flowName || n?.sourcePage || n?.source || n?.entityType || n?.type || '')
        .trim()
        .toLowerCase();
    if (!QUOTE_FLOWS.has(flow)) return null;
    return getNotificationOrderNumber(n);
};

const notificationLooksLikeSeenQuote = (n) => {
    if (getNotificationLineId(n) != null) return null;
    const orderNumber = getNotificationOrderNumber(n);
    if (!orderNumber || !seenQuoteIds.has(String(orderNumber))) return null;
    return String(orderNumber);
};

const notificationFlow = (n) => String(
    n?.flowName || n?.sourcePage || n?.source || n?.entityType || n?.type || ''
).trim().toLowerCase();

export const getNotificationPoLineId = (n) => {
    const value = n?.poLineId ?? n?.po_line_id ?? n?.polineId ?? null;
    return value == null || value === '' ? null : value;
};

const rememberPo = (poLineId, poNumber) => {
    if (poLineId != null && poLineId !== '') seenPoLineIds.add(String(poLineId));
    if (poNumber != null && poNumber !== '') seenPoNumbers.add(String(poNumber));
};

/** Purchase-order identity, when this notification is not a sales order or a quote. */
const getNotificationPo = (n) => {
    if (getNotificationQuoteId(n) || notificationLooksLikeSeenQuote(n)) return null;
    const explicitLine = getNotificationPoLineId(n);
    const orderNumber = getNotificationOrderNumber(n);
    const lineId = getNotificationLineId(n);
    if (explicitLine != null) {
        return { poLineId: String(explicitLine), poNumber: orderNumber };
    }
    if (PO_FLOWS.has(notificationFlow(n)) || n?._livePo) {
        return {
            poLineId: lineId != null ? String(lineId) : (n?.poLineId != null ? String(n.poLineId) : null),
            poNumber: orderNumber,
        };
    }
    if (lineId != null && seenPoLineIds.has(String(lineId))) {
        return { poLineId: String(lineId), poNumber: orderNumber };
    }
    if (lineId == null && orderNumber && seenPoNumbers.has(String(orderNumber))) {
        return { poLineId: null, poNumber: String(orderNumber) };
    }
    return null;
};

export const notificationMatchesThread = (n, thread) => {
    const quoteId = getNotificationQuoteId(n) || notificationLooksLikeSeenQuote(n);
    if (thread?.quoteId != null && thread.quoteId !== '') {
        return quoteId != null && String(quoteId) === String(thread.quoteId);
    }
    if (quoteId) return false;

    const po = getNotificationPo(n);
    if (thread?.poLineId != null && thread.poLineId !== '') {
        if (!po) return false;
        if (po.poLineId != null && String(po.poLineId) === String(thread.poLineId)) return true;
        return Boolean(
            po.poNumber
            && thread.orderNumber != null
            && String(po.poNumber) === String(thread.orderNumber)
        );
    }
    if (po) return false;

    return threadMatchesOrder(thread, {
        orderNumber: getNotificationOrderNumber(n),
        orderLineId: getNotificationLineId(n),
    });
};

const isOwnCommentEvent = (evt, user) => {
    const by = String(evt?.by?.email || evt?.by?.name || '').trim().toLowerCase();
    const me = (user || localStorage.getItem('userEmail') || localStorage.getItem('userName') || '').trim().toLowerCase();
    if (!by || !me) return false;
    const byKey = by.split('@')[0];
    const meKey = me.split('@')[0];
    return by === me || Boolean(byKey && byKey === meKey);
};

const liveIdKey = (id, prefix) => {
    const text = String(id);
    if (!text.startsWith(prefix)) return null;
    const rest = text.slice(prefix.length);
    const splitAt = rest.lastIndexOf(':');
    if (splitAt <= 0) return null;
    return rest.slice(0, splitAt);
};

const notificationTime = (n) => {
    const t = new Date(n?.createdAt || 0).getTime();
    return Number.isFinite(t) ? t : 0;
};

/** Two short tones, the same sound the bell has always played. */
const playNotificationSound = () => {
    try {
        const audioContext = new (window.AudioContext || window.webkitAudioContext)();
        const now = audioContext.currentTime;
        [[784, 0], [659, 0.15]].forEach(([frequency, offset]) => {
            const osc = audioContext.createOscillator();
            const gain = audioContext.createGain();
            osc.connect(gain);
            gain.connect(audioContext.destination);
            osc.frequency.value = frequency;
            osc.type = 'sine';
            gain.gain.setValueAtTime(0, now + offset);
            gain.gain.linearRampToValueAtTime(0.3, now + offset + 0.01);
            gain.gain.linearRampToValueAtTime(0.3, now + offset + 0.1);
            gain.gain.linearRampToValueAtTime(0, now + offset + 0.15);
            osc.start(now + offset);
            osc.stop(now + offset + 0.15);
        });
    } catch (error) {
        /* audio is optional (autoplay rules, no audio device) */
    }
};

/** Pick the order line a notification is about, from the lines of its order. */
const pickLineForNotification = (lines, { orderLineId, latestText }) => {
    if (!lines.length) return null;
    if (orderLineId != null) {
        const exact = lines.find((l) => String(l.id) === String(orderLineId));
        if (exact) return exact;
    }
    if (latestText) {
        const withText = lines.find((l) => (l.comment || []).some((c) => extractCommentText(c) === latestText));
        if (withText) return withText;
    }
    return lines.find((l) => Array.isArray(l.comment) && l.comment.length) || lines[0];
};

export function NotificationsProvider({ children }) {
    const { user } = useAuth() || {};
    const dock = useCommentDock();
    const dockRef = useRef(dock);
    dockRef.current = dock;
    const presenceCtx = usePresenceContext();
    const { fetchOrderValidationData } = orderContext();
    const fetchOrderRef = useRef(fetchOrderValidationData);
    fetchOrderRef.current = fetchOrderValidationData;

    const [rawNotifications, setRawNotifications] = useState(() => {
        const cached = readJson(CACHE_KEY, []);
        return Array.isArray(cached) ? cached : [];
    });
    // "Mark as unread" is local only (the API has no endpoint for it). Kept here so the
    // next poll does not undo it.
    const [forcedUnread, setForcedUnread] = useState(() => new Set(readJson(FORCED_UNREAD_KEY, []).map(String)));
    const [attentionIds, setAttentionIds] = useState(() => {
        const email = (localStorage.getItem('userEmail') || '').trim().toLowerCase();
        return new Set(readJson(attentionStorageKey(email), []).map(String));
    });
    const [userDepartments, setUserDepartments] = useState([]);
    // Comments noticed live, before (or without) a server notification row.
    // Keeps the minimized chip blue until this user opens or dismisses that chat.
    const [liveQuoteAttention, setLiveQuoteAttention] = useState({});
    const [liveOrderAttention, setLiveOrderAttention] = useState({});
    const [livePoAttention, setLivePoAttention] = useState({});

    const signatureRef = useRef('');
    const inFlightRef = useRef(false);
    const queuedRef = useRef(false);
    const refreshTimerRef = useRef(null);
    const knownIdsRef = useRef(null);
    const reloadAtRef = useRef({});
    const newShownKeysRef = useRef(new Set());
    const notificationsRef = useRef([]);

    const userEmail = (user || localStorage.getItem('userEmail') || '').trim().toLowerCase();
    const currentUserKey = userEmail.split('@')[0];

    const storeNotifications = useCallback((data) => {
        const signature = JSON.stringify(data);
        if (signature === signatureRef.current) return;
        signatureRef.current = signature;
        setRawNotifications(data);
        writeJson(CACHE_KEY, data);
    }, []);

    const refresh = useCallback(async () => {
        const email = (user || localStorage.getItem('userEmail') || '').trim();
        if (!email) return;
        if (inFlightRef.current) {
            queuedRef.current = true;
            return;
        }
        inFlightRef.current = true;
        try {
            const data = await fetchNotifications(email, { rethrow: true });
            if (Array.isArray(data)) storeNotifications(data);
        } catch (err) {
            /* keep what we have; the next poll retries */
        } finally {
            inFlightRef.current = false;
            if (queuedRef.current) {
                queuedRef.current = false;
                refresh();
            }
        }
    }, [user, storeNotifications]);

    const scheduleRefresh = useCallback((delay = 0) => {
        clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = setTimeout(() => refresh(), delay);
    }, [refresh]);

    // Poll while the tab is visible, and catch up as soon as it becomes visible again.
    useEffect(() => {
        refresh();
        const onVisible = () => {
            if (document.visibilityState === 'visible') refresh();
        };
        const interval = setInterval(onVisible, POLL_MS);
        document.addEventListener('visibilitychange', onVisible);
        window.addEventListener('focus', onVisible);
        return () => {
            clearInterval(interval);
            clearTimeout(refreshTimerRef.current);
            document.removeEventListener('visibilitychange', onVisible);
            window.removeEventListener('focus', onVisible);
        };
    }, [refresh]);

    // Other tabs of this browser.
    useEffect(() => {
        const onStorage = (e) => {
            if (e.key === CACHE_KEY) {
                const next = readJson(CACHE_KEY, []);
                signatureRef.current = JSON.stringify(next);
                setRawNotifications(Array.isArray(next) ? next : []);
            } else if (e.key === FORCED_UNREAD_KEY) {
                setForcedUnread(new Set(readJson(FORCED_UNREAD_KEY, []).map(String)));
            }
        };
        window.addEventListener('storage', onStorage);
        return () => window.removeEventListener('storage', onStorage);
    }, []);

    // Someone saved a comment: refresh server notifications, and pop or update one
    // minimized chat immediately. Quotes, orders, and purchase orders share this path.
    const subscribeSave = presenceCtx?.subscribeSave;
    useEffect(() => {
        if (!subscribeSave) return undefined;
        return subscribeSave((evt) => {
            if (evt?.kind !== 'comment') return;
            scheduleRefresh(PRESENCE_REFRESH_DELAY_MS);

            const quoteId = evt.quoteId != null && evt.quoteId !== '' ? String(evt.quoteId).trim() : '';
            const poLineId = evt.poLineId != null && evt.poLineId !== '' ? String(evt.poLineId).trim() : '';
            const orderNumber = evt.orderNumber != null && String(evt.orderNumber).trim()
                ? String(evt.orderNumber).trim()
                : '';
            const lineId = evt.lineId != null && evt.lineId !== '' ? String(evt.lineId).trim() : '';
            if (!quoteId && !poLineId && !orderNumber && !lineId) return;
            if (isOwnCommentEvent(evt, user)) return;

            const incoming = normalizeComment(evt.value) || {};
            if (!extractCommentText(incoming)) incoming.comment = extractCommentText(evt.value);
            if (!incoming.lastEditedBy) incoming.lastEditedBy = evt.by?.name || evt.by?.email || '';

            const dockApi = dockRef.current;
            let desiredKey = null;
            let fields = null;
            let match = null;

            if (quoteId) {
                rememberQuoteId(quoteId);
                desiredKey = getCommentThreadKey({ quoteId });
                fields = {
                    quoteId,
                    orderNumber: quoteId,
                    title: String(quoteId),
                    sourcePage: 'quotes',
                };
                match = (t) => t.key === desiredKey || (t.quoteId != null && String(t.quoteId) === quoteId);
            } else if (poLineId) {
                rememberPo(poLineId, orderNumber);
                desiredKey = getCommentThreadKey({ poLineId });
                fields = {
                    poLineId,
                    orderNumber: orderNumber || undefined,
                    title: orderNumber || `PO ${poLineId}`,
                    sourcePage: 'purchase',
                };
                match = (t) => t.key === desiredKey
                    || (t.poLineId != null && String(t.poLineId) === poLineId);
            } else {
                desiredKey = lineId
                    ? getCommentThreadKey({ orderLineId: lineId })
                    : getOrderThreadKey(orderNumber);
                fields = {
                    orderLineId: lineId || undefined,
                    orderNumber: orderNumber || undefined,
                    title: orderNumber || lineId,
                    sourcePage: 'orders',
                };
                match = (t) => {
                    if (t.quoteId != null && t.quoteId !== '') return false;
                    if (t.poLineId != null && t.poLineId !== '') return false;
                    if (t.key === desiredKey) return true;
                    return threadMatchesOrder(t, { orderNumber, orderLineId: lineId });
                };
            }

            const existing = (dockApi?.threads || []).find(match);
            const key = existing?.key || desiredKey;
            const prevComments = Array.isArray(existing?.comments) ? existing.comments : [];
            const incomingId = getCommentId(incoming);
            const alreadyThere = incomingId != null && prevComments.some(
                (comment) => String(getCommentId(comment)) === String(incomingId)
            );
            const comments = alreadyThere ? prevComments : [incoming, ...prevComments];
            const subtitle = evt.customerName || existing?.subtitle || '';

            // Pop the minimized chat immediately. An already-open window stays open
            // and just receives the new comment. An existing minimized chat is updated.
            dockApi?.ensureThread?.({
                key,
                ...fields,
                subtitle,
                comments,
            });
            dockApi?.patchThread?.(key, {
                ...fields,
                comments,
                subtitle,
                orderNumber: fields.orderNumber || existing?.orderNumber,
                pendingLoad: false,
                loading: false,
                loadError: null,
            });

            if (existing && !existing.minimized) return;

            if (quoteId) {
                setLiveQuoteAttention((prev) => {
                    const current = prev[quoteId];
                    return {
                        ...prev,
                        [quoteId]: {
                            count: (current?.count || 0) + (alreadyThere ? 0 : 1),
                            comment: extractCommentText(incoming) || current?.comment || '',
                            mentionedBy: incoming.lastEditedBy || current?.mentionedBy || '',
                            customerName: evt.customerName || current?.customerName || '',
                            at: evt.at || Date.now(),
                        },
                    };
                });
                return;
            }

            if (poLineId) {
                setLivePoAttention((prev) => {
                    const current = prev[poLineId];
                    return {
                        ...prev,
                        [poLineId]: {
                            poLineId,
                            orderNumber: orderNumber || current?.orderNumber || '',
                            count: (current?.count || 0) + (alreadyThere ? 0 : 1),
                            comment: extractCommentText(incoming) || current?.comment || '',
                            mentionedBy: incoming.lastEditedBy || current?.mentionedBy || '',
                            customerName: evt.customerName || current?.customerName || '',
                            at: evt.at || Date.now(),
                        },
                    };
                });
                return;
            }

            const orderKey = orderNumber || `line-${lineId}`;
            setLiveOrderAttention((prev) => {
                const current = prev[orderKey];
                return {
                    ...prev,
                    [orderKey]: {
                        orderNumber: orderNumber || current?.orderNumber || '',
                        orderLineId: lineId || current?.orderLineId || '',
                        count: (current?.count || 0) + (alreadyThere ? 0 : 1),
                        comment: extractCommentText(incoming) || current?.comment || '',
                        mentionedBy: incoming.lastEditedBy || current?.mentionedBy || '',
                        customerName: evt.customerName || current?.customerName || '',
                        at: evt.at || Date.now(),
                    },
                };
            });
        });
    }, [subscribeSave, scheduleRefresh, user]);

    // A web push reached the service worker (see public/service-worker.js).
    useEffect(() => {
        if (!('serviceWorker' in navigator)) return undefined;
        const onMessage = (e) => {
            if (e?.data?.type === 'bridge:push') scheduleRefresh(300);
        };
        navigator.serviceWorker.addEventListener('message', onMessage);
        return () => navigator.serviceWorker.removeEventListener('message', onMessage);
    }, [scheduleRefresh]);

    // Department membership, for @department mentions.
    useEffect(() => {
        if (!userEmail) return;
        let cancelled = false;
        getUsersByEmail(userEmail)
            .then((result) => {
                if (cancelled || !result?.success || !result?.user?.department) return;
                setUserDepartments(result.user.department
                    .toString()
                    .split(',')
                    .map((d) => d.trim().toLowerCase())
                    .filter(Boolean));
            })
            .catch(() => { /* department mentions simply won't match */ });
        return () => { cancelled = true; };
    }, [userEmail]);

    const notifications = useMemo(() => rawNotifications
        .map((n) => (forcedUnread.has(String(getNotificationId(n))) ? { ...n, readFlag: false } : n))
        .slice()
        .sort((a, b) => notificationTime(b) - notificationTime(a))
        .map((n) => {
            const commentLower = (n.comment || '').toString().toLowerCase();
            const mentionedByText = (n.mentionedBy || '').toString().toLowerCase();
            const isMention = Boolean(
                (userEmail && mentionedByText === userEmail)
                || (currentUserKey && mentionedByText === currentUserKey)
                || (currentUserKey && commentLower.includes(`@${currentUserKey}`))
                || userDepartments.some((dept) => commentLower.includes(`@${dept}`)
                    || commentLower.includes(`@${dept.replace(/\s+/g, '')}`)
                    || mentionedByText === dept)
            );
            return { ...n, isMention };
        }), [rawNotifications, forcedUnread, userEmail, currentUserKey, userDepartments]);

    const unread = useMemo(() => notifications.filter((n) => !n.readFlag), [notifications]);
    notificationsRef.current = notifications;

    // Comments the user has not opened yet. A minimized chat stays in this set until
    // that user opens or dismisses it, so the chip stays blue and the bell keeps its count.
    const pending = useMemo(() => {
        const apiPending = notifications.filter((n) => {
            const id = String(getNotificationId(n) ?? '');
            return !n.readFlag || (id && attentionIds.has(id));
        });
        const coveredQuotes = new Set(
            apiPending.map((n) => getNotificationQuoteId(n) || notificationLooksLikeSeenQuote(n)).filter(Boolean)
        );
        const coveredOrderNumbers = new Set();
        const coveredOrderLines = new Set();
        const coveredPoLines = new Set();
        const coveredPoNumbers = new Set();
        apiPending.forEach((n) => {
            if (getNotificationQuoteId(n) || notificationLooksLikeSeenQuote(n)) return;
            const po = getNotificationPo(n);
            if (po) {
                if (po.poLineId) coveredPoLines.add(String(po.poLineId));
                if (po.poNumber) coveredPoNumbers.add(String(po.poNumber));
                return;
            }
            const num = getNotificationOrderNumber(n);
            const line = getNotificationLineId(n);
            if (num) coveredOrderNumbers.add(String(num));
            if (line != null) coveredOrderLines.add(String(line));
        });
        const copiesOf = (count) => Math.min(Math.max(count || 1, 1), 99);
        const liveQuotes = Object.entries(liveQuoteAttention)
            .filter(([quoteId]) => !coveredQuotes.has(quoteId))
            .flatMap(([quoteId, item]) => Array.from({ length: copiesOf(item?.count) }, (_, index) => ({
                notificationId: `live-quote:${quoteId}:${index}`,
                quoteId,
                flowName: 'Quote',
                customerName: item.customerName || '',
                comment: item.comment || '',
                mentionedBy: item.mentionedBy || '',
                createdAt: new Date(item.at || Date.now()).toISOString(),
                readFlag: false,
                _live: true,
            })));
        const liveOrders = Object.entries(liveOrderAttention)
            .filter(([, item]) => {
                if (item?.orderNumber && coveredOrderNumbers.has(String(item.orderNumber))) return false;
                if (item?.orderLineId && coveredOrderLines.has(String(item.orderLineId))) return false;
                return true;
            })
            .flatMap(([key, item]) => Array.from({ length: copiesOf(item?.count) }, (_, index) => ({
                notificationId: `live-order:${key}:${index}`,
                orderNumber: item.orderNumber || null,
                orderLineId: item.orderLineId || null,
                flowName: 'Order',
                customerName: item.customerName || '',
                comment: item.comment || '',
                mentionedBy: item.mentionedBy || '',
                createdAt: new Date(item.at || Date.now()).toISOString(),
                readFlag: false,
                _live: true,
            })));
        const livePos = Object.entries(livePoAttention)
            .filter(([poLineId, item]) => {
                if (coveredPoLines.has(String(poLineId))) return false;
                if (item?.orderNumber && coveredPoNumbers.has(String(item.orderNumber))) return false;
                return true;
            })
            .flatMap(([poLineId, item]) => Array.from({ length: copiesOf(item?.count) }, (_, index) => ({
                notificationId: `live-po:${poLineId}:${index}`,
                poLineId,
                orderNumber: item.orderNumber || null,
                flowName: 'Purchase Order',
                customerName: item.customerName || '',
                comment: item.comment || '',
                mentionedBy: item.mentionedBy || '',
                createdAt: new Date(item.at || Date.now()).toISOString(),
                readFlag: false,
                _live: true,
                _livePo: true,
            })));
        return [...liveQuotes, ...liveOrders, ...livePos, ...apiPending];
    }, [notifications, attentionIds, liveQuoteAttention, liveOrderAttention, livePoAttention]);

    const clearLiveQuote = useCallback((quoteId) => {
        if (quoteId == null || quoteId === '') return;
        const id = String(quoteId);
        setLiveQuoteAttention((prev) => {
            if (!prev[id]) return prev;
            const next = { ...prev };
            delete next[id];
            return next;
        });
    }, []);

    const clearLiveOrder = useCallback((orderNumber, orderLineId) => {
        setLiveOrderAttention((prev) => {
            const next = { ...prev };
            let changed = false;
            Object.keys(next).forEach((key) => {
                const item = next[key];
                const numberMatch = orderNumber && (
                    key === String(orderNumber) || String(item?.orderNumber || '') === String(orderNumber)
                );
                const lineMatch = orderLineId && String(item?.orderLineId || '') === String(orderLineId);
                if (!numberMatch && !lineMatch) return;
                delete next[key];
                changed = true;
            });
            return changed ? next : prev;
        });
    }, []);

    const clearLivePo = useCallback((poLineId, orderNumber) => {
        setLivePoAttention((prev) => {
            const next = { ...prev };
            let changed = false;
            Object.keys(next).forEach((key) => {
                const item = next[key];
                const lineMatch = poLineId && (key === String(poLineId) || String(item?.poLineId || '') === String(poLineId));
                const numberMatch = !poLineId && orderNumber && String(item?.orderNumber || '') === String(orderNumber);
                if (!lineMatch && !numberMatch) return;
                delete next[key];
                changed = true;
            });
            return changed ? next : prev;
        });
    }, []);

    const clearAttentionForThread = useCallback((thread) => {
        if (!thread) return;
        if (thread.quoteId != null && thread.quoteId !== '') clearLiveQuote(thread.quoteId);
        if (thread.poLineId != null && thread.poLineId !== '') clearLivePo(thread.poLineId, thread.orderNumber);
        else if (thread.quoteId == null || thread.quoteId === '') clearLiveOrder(thread.orderNumber, thread.orderLineId);
        setAttentionIds((prev) => {
            if (!prev.size) return prev;
            const next = new Set([...prev].filter((id) => {
                const match = notificationsRef.current.find((item) => String(getNotificationId(item)) === id);
                return !(match && notificationMatchesThread(match, thread));
            }));
            if (next.size === prev.size) return prev;
            writeJson(attentionStorageKey(userEmail), [...next]);
            return next;
        });
    }, [userEmail, clearLiveQuote, clearLiveOrder, clearLivePo]);

    // First time an open chat shows these comments, remember them so a refresh
    // does not paint the "New" pill again.
    const noteOpenedNewComments = useCallback((matches) => {
        const ids = matches.map((n) => String(getNotificationId(n) ?? '')).filter(Boolean);
        if (!ids.length) return 0;
        const key = seenNewStorageKey(userEmail);
        const persisted = new Set(readJson(key, []).map(String));
        let changed = false;
        ids.forEach((id) => {
            if (!persisted.has(id)) {
                persisted.add(id);
                shownNewThisPageLoad.add(id);
                changed = true;
            }
        });
        if (changed) writeJson(key, [...persisted]);
        return ids.filter((id) => shownNewThisPageLoad.has(id)).length;
    }, [userEmail]);

    // Bell sound when a notification we have never seen arrives unread. On first load it
    // plays once if anything is unread, as the bell always has.
    useEffect(() => {
        const known = knownIdsRef.current;
        const arrived = unread.some((n) => !known || !known.has(String(getNotificationId(n))));
        knownIdsRef.current = new Set(notifications.map((n) => String(getNotificationId(n))));
        if (arrived) playNotificationSound();
    }, [notifications, unread]);

    /** Unread notifications grouped into one tray chat each: a quote, a purchase order, or an order. */
    const unreadGroups = useMemo(() => {
        const orders = new Map();
        const quotes = new Map();
        const purchaseOrders = new Map();
        pending.forEach((n) => {
            const quoteId = getNotificationQuoteId(n) || notificationLooksLikeSeenQuote(n);
            if (quoteId) {
                rememberQuoteId(quoteId);
                const group = quotes.get(quoteId) || {
                    kind: 'quote', quoteId, customerName: null, count: 0, ids: [], latest: null,
                };
                group.count += 1;
                if (!n._live) group.ids.push(getNotificationId(n));
                group.customerName = group.customerName || n.customerName || null;
                if (!group.latest || notificationTime(n) > notificationTime(group.latest)) group.latest = n;
                quotes.set(quoteId, group);
                return;
            }
            const po = getNotificationPo(n);
            if (po && (po.poLineId || po.poNumber)) {
                rememberPo(po.poLineId, po.poNumber);
                const groupKey = po.poLineId || `num:${po.poNumber}`;
                const group = purchaseOrders.get(groupKey) || {
                    kind: 'po', poLineId: po.poLineId, orderNumber: po.poNumber, customerName: null, count: 0, ids: [], latest: null,
                };
                group.count += 1;
                if (!n._live) group.ids.push(getNotificationId(n));
                group.poLineId = group.poLineId || po.poLineId;
                group.orderNumber = group.orderNumber || po.poNumber;
                group.customerName = group.customerName || n.customerName || null;
                if (!group.latest || notificationTime(n) > notificationTime(group.latest)) group.latest = n;
                purchaseOrders.set(groupKey, group);
                return;
            }
            const orderNumber = getNotificationOrderNumber(n);
            if (!orderNumber) return;
            const group = orders.get(orderNumber) || {
                kind: 'order', orderNumber, orderLineId: null, customerName: null, count: 0, ids: [], latest: null,
            };
            group.count += 1;
            if (!n._live) group.ids.push(getNotificationId(n));
            group.orderLineId = group.orderLineId || getNotificationLineId(n);
            group.customerName = group.customerName || n.customerName || null;
            if (!group.latest || notificationTime(n) > notificationTime(group.latest)) group.latest = n;
            orders.set(orderNumber, group);
        });
        return [...quotes.values(), ...purchaseOrders.values(), ...orders.values()];
    }, [pending]);

    const markRead = useCallback((ids) => {
        const list = (Array.isArray(ids) ? ids : [ids]).filter((id) => id != null).map(String);
        if (!list.length) return;
        const liveQuoteIds = new Set();
        const liveOrderKeys = new Set();
        const livePoKeys = new Set();
        list.forEach((id) => {
            const quoteId = liveIdKey(id, 'live-quote:');
            const orderKey = liveIdKey(id, 'live-order:');
            const poKey = liveIdKey(id, 'live-po:');
            if (quoteId) liveQuoteIds.add(quoteId);
            if (orderKey) liveOrderKeys.add(orderKey);
            if (poKey) livePoKeys.add(poKey);
        });
        if (liveQuoteIds.size) {
            setLiveQuoteAttention((prev) => {
                const next = { ...prev };
                let changed = false;
                liveQuoteIds.forEach((quoteId) => {
                    if (next[quoteId]) {
                        delete next[quoteId];
                        changed = true;
                    }
                });
                return changed ? next : prev;
            });
        }
        if (liveOrderKeys.size) {
            setLiveOrderAttention((prev) => {
                const next = { ...prev };
                let changed = false;
                liveOrderKeys.forEach((key) => {
                    if (next[key]) {
                        delete next[key];
                        changed = true;
                    }
                });
                return changed ? next : prev;
            });
        }
        if (livePoKeys.size) {
            setLivePoAttention((prev) => {
                const next = { ...prev };
                let changed = false;
                livePoKeys.forEach((key) => {
                    if (next[key]) {
                        delete next[key];
                        changed = true;
                    }
                });
                return changed ? next : prev;
            });
        }
        const serverList = list.filter((id) => (
            !id.startsWith('live-quote:') && !id.startsWith('live-order:') && !id.startsWith('live-po:')
        ));
        if (!serverList.length) return;
        const wanted = new Set(serverList);
        setForcedUnread((prev) => {
            if (!serverList.some((id) => prev.has(id))) return prev;
            const next = new Set([...prev].filter((id) => !wanted.has(id)));
            writeJson(FORCED_UNREAD_KEY, [...next]);
            return next;
        });
        setRawNotifications((prev) => {
            const next = prev.map((n) => (wanted.has(String(getNotificationId(n))) && !n.readFlag ? { ...n, readFlag: true } : n));
            signatureRef.current = JSON.stringify(next);
            writeJson(CACHE_KEY, next);
            return next;
        });
        serverList.forEach((id) => {
            markNotificationRead(id).catch(() => { /* the next poll shows the real state */ });
        });
    }, []);

    const markUnread = useCallback((ids) => {
        const list = (Array.isArray(ids) ? ids : [ids]).filter((id) => id != null).map(String);
        if (!list.length) return;
        setForcedUnread((prev) => {
            const next = new Set([...prev, ...list]);
            writeJson(FORCED_UNREAD_KEY, [...next]);
            return next;
        });
    }, []);

    const markAllRead = useCallback(() => {
        markRead(unread.map(getNotificationId));
    }, [markRead, unread]);

    const markAllUnread = useCallback(() => {
        markUnread(notifications.map(getNotificationId));
    }, [markUnread, notifications]);

    const unreadForThread = useCallback(
        (thread) => pending.filter((n) => notificationMatchesThread(n, thread)),
        [pending]
    );

    const unreadCountForOrder = useCallback((orderNumber, orderLineId) => pending.filter((n) => {
        if (getNotificationQuoteId(n) || notificationLooksLikeSeenQuote(n) || getNotificationPo(n)) return false;
        const num = getNotificationOrderNumber(n);
        const line = getNotificationLineId(n);
        if (line != null && orderLineId != null) return String(line) === String(orderLineId);
        return num != null && orderNumber != null && String(num) === String(orderNumber);
    }).length, [pending]);

    const unreadCountForQuote = useCallback((quoteId) => pending.filter((n) => {
        const id = getNotificationQuoteId(n) || notificationLooksLikeSeenQuote(n);
        return id != null && quoteId != null && String(id) === String(quoteId);
    }).length, [pending]);

    /* ---------------- dock integration ---------------- */

    const threads = dock?.threads;
    const threadsRef = useRef(threads);
    threadsRef.current = threads;
    const loadThreadRef = useRef(null);
    const ensureThread = dock?.ensureThread;
    const patchThread = dock?.patchThread;
    const rekeyThread = dock?.rekeyThread;
    const openThread = dock?.openThread;
    const restoreThread = dock?.restoreThread;
    const closeThread = dock?.closeThread;

    /** Load a chat's comments from the server (used for chats opened from a notification). */
    const loadThreadComments = useCallback(async (thread, { highlightCount, force = false } = {}) => {
        if (!thread || !patchThread) return;
        if (thread.poLineId) return;
        if (!thread.quoteId && !thread.orderNumber) return;
        const now = Date.now();
        if (!force && reloadAtRef.current[thread.key] && now - reloadAtRef.current[thread.key] < RELOAD_GUARD_MS) return;
        reloadAtRef.current[thread.key] = now;

        if (thread.quoteId) {
            patchThread(thread.key, { loading: true, loadError: null });
            try {
                const quote = await fetchQuoteById(thread.quoteId);
                if (!quote) {
                    patchThread(thread.key, {
                        loading: false,
                        loadError: `Quote ${thread.quoteId} could not be found.`,
                    });
                    return;
                }
                const serverComments = (Array.isArray(quote.comment) ? quote.comment : [])
                    .filter((comment) => comment && !isQuoteLikeRecord(comment))
                    .map(normalizeComment);
                const serverIds = new Set(serverComments.map((comment) => String(getCommentId(comment) ?? '')));
                const localOnly = (thread.comments || []).filter((comment) => {
                    const id = String(getCommentId(comment) ?? '');
                    return id && !serverIds.has(id);
                });
                const comments = sortCommentsNewestFirst([...localOnly, ...serverComments]);
                const patch = {
                    title: thread.title || String(quote.id || thread.quoteId),
                    subtitle: quote.customer_name || thread.subtitle || '',
                    customerId: quote.customer_id ?? thread.customerId,
                    orderNumber: thread.orderNumber || String(quote.id || thread.quoteId),
                    comments,
                    loading: false,
                    loadError: null,
                    pendingLoad: false,
                };
                if (highlightCount != null) patch.highlightCount = highlightCount;
                patchThread(thread.key, patch);
            } catch (err) {
                patchThread(thread.key, {
                    loading: false,
                    loadError: 'Comments could not be loaded. Close the chat and try again.',
                });
            }
            return;
        }

        patchThread(thread.key, { loading: true, loadError: null });
        try {
            const result = await fetchOrderRef.current(
                0, 10, '', 'lineitem', 'ascending', null, '', '', '', '', '', '', '', '', '', true, true, '', thread.orderNumber
            );
            const lines = Array.isArray(result?.order) ? result.order : [];
            const latest = unread.find((n) => notificationMatchesThread(n, thread));
            const line = pickLineForNotification(lines, {
                orderLineId: thread.orderLineId,
                latestText: latest ? extractCommentText(latest.comment) : '',
            });
            if (!line) {
                patchThread(thread.key, {
                    loading: false,
                    loadError: `Order ${thread.orderNumber} could not be found. It may be archived or no longer open.`,
                });
                return;
            }
            const patch = {
                orderLineId: line.id,
                // Keep the order number the notification used, so the chat keeps matching
                // its notifications (and Rule 1 never adds a second chat for the same order).
                orderNumber: thread.orderNumber || line.order?.ordernum,
                title: thread.title || line.order?.ordernum,
                subtitle: line.order?.customer?.name || thread.subtitle || '',
                customerId: line.order?.customer?.id ?? thread.customerId,
                comments: sortCommentsNewestFirst((line.comment || []).map(normalizeComment)),
                loading: false,
                loadError: null,
                pendingLoad: false,
            };
            if (highlightCount != null) patch.highlightCount = highlightCount;
            const lineKey = getCommentThreadKey({ orderLineId: line.id });
            if (lineKey && lineKey !== thread.key) rekeyThread(thread.key, lineKey, patch);
            else patchThread(thread.key, patch);
        } catch (err) {
            patchThread(thread.key, {
                loading: false,
                loadError: 'Comments could not be loaded. Close the chat and try again.',
            });
        }
    }, [patchThread, rekeyThread, unread]);
    loadThreadRef.current = loadThreadComments;

    // Keep a new comment blue on other users' minimized chats until they open it.
    useEffect(() => {
        if (!unread.length) return undefined;
        const openThreads = (threads || []).filter((t) => !t.minimized);
        const seen = new Set(readJson(seenNewStorageKey(userEmail), []).map(String));
        setAttentionIds((prev) => {
            const next = new Set(prev);
            let changed = false;
            unread.forEach((n) => {
                const id = String(getNotificationId(n) ?? '');
                if (!id || next.has(id) || seen.has(id)) return;
                const onScreen = openThreads.some((t) => notificationMatchesThread(n, t));
                if (onScreen) return;
                next.add(id);
                changed = true;
            });
            if (!changed) return prev;
            writeJson(attentionStorageKey(userEmail), [...next]);
            return next;
        });
        return undefined;
    }, [unread, threads, userEmail]);

    // Drop attention ids whose notification is no longer in the list.
    useEffect(() => {
        if (!notifications.length) return undefined;
        const live = new Set(notifications.map((n) => String(getNotificationId(n) ?? '')).filter(Boolean));
        setAttentionIds((prev) => {
            if (!prev.size) return prev;
            const next = new Set([...prev].filter((id) => live.has(id)));
            if (next.size === prev.size) return prev;
            writeJson(attentionStorageKey(userEmail), [...next]);
            return next;
        });
        return undefined;
    }, [notifications, userEmail]);

    // Rule 1: every order or quote with unread comments has a (minimized) chat in the tray.
    useEffect(() => {
        if (!ensureThread) return;
        unreadGroups.forEach((group) => {
            if (group.kind === 'quote') {
                ensureThread({
                    key: getCommentThreadKey({ quoteId: group.quoteId }),
                    quoteId: group.quoteId,
                    orderNumber: group.quoteId,
                    title: String(group.quoteId),
                    subtitle: group.customerName || '',
                    sourcePage: 'quotes',
                });
                return;
            }
            if (group.kind === 'po') {
                ensureThread({
                    key: group.poLineId
                        ? getCommentThreadKey({ poLineId: group.poLineId })
                        : getOrderThreadKey(group.orderNumber),
                    poLineId: group.poLineId || undefined,
                    orderNumber: group.orderNumber || undefined,
                    title: group.orderNumber || (group.poLineId ? `PO ${group.poLineId}` : 'PO'),
                    subtitle: group.customerName || '',
                    sourcePage: 'purchase',
                });
                return;
            }
            ensureThread({
                key: group.orderLineId
                    ? getCommentThreadKey({ orderLineId: group.orderLineId })
                    : getOrderThreadKey(group.orderNumber),
                orderNumber: group.orderNumber,
                orderLineId: group.orderLineId || undefined,
                title: group.orderNumber,
                subtitle: group.customerName || '',
                sourcePage: 'orders',
            });
        });
    }, [unreadGroups, ensureThread]);

    // Rule 3: a chat open on screen is read. New comments for it are loaded into the window.
    // Minimized chats created from a notification are loaded once in the background so
    // their chip can show the customer name and the newest comment.
    useEffect(() => {
        if (!Array.isArray(threads)) return;
        const toRead = [];
        threads.forEach((thread) => {
            if (thread.minimized) {
                if (thread.pendingLoad && !thread.subtitle && !thread.loading && !thread.loadError && thread.orderNumber) {
                    loadThreadComments(thread);
                }
                return;
            }
            clearAttentionForThread(thread);
            const matches = unread.filter((n) => notificationMatchesThread(n, thread));
            if (matches.length) {
                toRead.push(...matches.map(getNotificationId));
                const ids = matches.map((n) => String(getNotificationId(n) ?? '')).filter(Boolean);
                const highlight = noteOpenedNewComments(matches);
                const fresh = ids.some((id) => shownNewThisPageLoad.has(id) && !newShownKeysRef.current.has(id));
                if (fresh && highlight > 0) {
                    ids.forEach((id) => {
                        if (shownNewThisPageLoad.has(id)) newShownKeysRef.current.add(id);
                    });
                    patchThread(thread.key, { highlightCount: highlight });
                }
                loadThreadComments(thread);
            } else if (thread.pendingLoad && !thread.loading && !thread.loadError) {
                loadThreadComments(thread);
            }
        });
        if (toRead.length) markRead(toRead);
    }, [threads, unread, loadThreadComments, markRead, clearAttentionForThread, noteOpenedNewComments, patchThread]);

    /** Open (or restore) the chat for an order or quote, from the bell or the tray. */
    const openOrderChat = useCallback(({ orderNumber, orderLineId, quoteId, poLineId, key } = {}) => {
        if (!Array.isArray(threads)) return;
        const existing = threads.find((t) => {
            if (key && t.key === key) return true;
            if (quoteId != null && quoteId !== '' && t.quoteId != null && String(t.quoteId) === String(quoteId)) return true;
            if (poLineId != null && poLineId !== '' && t.poLineId != null && String(t.poLineId) === String(poLineId)) return true;
            if (quoteId) return false;
            if (t.quoteId != null && t.quoteId !== '') return false;
            if (t.poLineId != null && t.poLineId !== '') {
                if (orderLineId != null && String(t.poLineId) === String(orderLineId)) return true;
                if (orderLineId == null && orderNumber && String(t.orderNumber) === String(orderNumber)) return true;
                return false;
            }
            if (poLineId) return false;
            return threadMatchesOrder(t, { orderNumber, orderLineId });
        });
        if (existing) {
            restoreThread(existing.key);
            return;
        }
        if (quoteId != null && quoteId !== '') {
            openThread({
                key: getCommentThreadKey({ quoteId }),
                expand: true,
                quoteId,
                orderNumber: orderNumber || quoteId,
                title: String(quoteId),
                sourcePage: 'quotes',
                pendingLoad: true,
            });
            return;
        }
        if (poLineId != null && poLineId !== '') {
            openThread({
                key: getCommentThreadKey({ poLineId }),
                expand: true,
                poLineId,
                orderNumber: orderNumber || undefined,
                title: orderNumber || `PO ${poLineId}`,
                sourcePage: 'purchase',
                pendingLoad: true,
            });
            return;
        }
        const threadKey = orderLineId ? getCommentThreadKey({ orderLineId }) : getOrderThreadKey(orderNumber);
        if (!threadKey) return;
        openThread({
            key: threadKey,
            expand: true,
            orderNumber,
            orderLineId: orderLineId || undefined,
            title: orderNumber,
            sourcePage: 'orders',
            pendingLoad: true,
        });
    }, [threads, restoreThread, openThread]);

    /** Rule 4: dismissing a chat from the tray marks it read. */
    const dismissThread = useCallback((thread) => {
        if (!thread) return;
        clearAttentionForThread(thread);
        markRead(unreadForThread(thread).map(getNotificationId));
        closeThread(thread.key);
    }, [markRead, unreadForThread, closeThread, clearAttentionForThread]);

    const value = useMemo(() => ({
        notifications,
        unreadCount: pending.length,
        unreadMentionCount: pending.filter((n) => n.isMention).length,
        unreadGroups,
        unreadForThread,
        unreadCountForOrder,
        unreadCountForQuote,
        markRead,
        markUnread,
        markAllRead,
        markAllUnread,
        refresh,
        openOrderChat,
        dismissThread,
        isOrderOnlyThreadKey,
    }), [notifications, pending, unreadGroups, unreadForThread, unreadCountForOrder, unreadCountForQuote, markRead, markUnread,
        markAllRead, markAllUnread, refresh, openOrderChat, dismissThread]);

    return (
        <NotificationsContext.Provider value={value}>
            {children}
        </NotificationsContext.Provider>
    );
}

export function useNotifications() {
    return useContext(NotificationsContext);
}

export default NotificationsContext;
